<template>
  <div class="model-load-status">
    <!-- Sign-in Required -->
    <div v-if="!hasSession" class="alert alert-warning d-flex align-items-center">
      <i class="bi bi-lock me-3 fs-4"></i>
      <div class="flex-grow-1">
        <strong>Sign in required</strong>
        <p class="mb-0 small">Please sign in to access model load status.</p>
      </div>
      <a class="btn btn-primary btn-sm" href="#" onclick="window.dialogManager?.showSignIn(); return false;">
        <i class="bi bi-box-arrow-in-right me-1"></i>Sign In
      </a>
    </div>

    <template v-else>
      <div class="status-header">
        <h5><i class="bi bi-activity me-2"></i>Model Load Status</h5>
        <div class="header-controls">
          <label class="auto-refresh-toggle">
            <input type="checkbox" v-model="autoRefresh" />
            <span>Auto-refresh ({{ refreshIntervalSecs }}s)</span>
          </label>
          <button class="btn btn-sm btn-outline-primary" @click="refresh" :disabled="isLoading">
            <i class="bi" :class="isLoading ? 'bi-arrow-repeat spin-icon' : 'bi-arrow-clockwise'"></i>
            Refresh
          </button>
          <span v-if="lastRefreshTime" class="last-refresh text-muted small">
            Updated {{ formatTimeAgo(lastRefreshTime) }}
          </span>
        </div>
      </div>

      <div v-if="isLoading && !loaderData.length" class="text-center py-4">
        <i class="bi bi-arrow-repeat spin-icon fs-4"></i>
        <p class="text-muted mt-2">Loading model status...</p>
      </div>

      <div v-else-if="errorMessage" class="alert alert-danger">
        <i class="bi bi-exclamation-triangle me-2"></i>{{ errorMessage }}
      </div>

      <template v-else-if="loaderData.length">
        <!-- Summary cards -->
        <div class="summary-cards">
          <div class="summary-card">
            <div class="card-value">{{ loaderData.length }}</div>
            <div class="card-label">Total Models</div>
          </div>
          <div class="summary-card success">
            <div class="card-value">{{ successCount }}</div>
            <div class="card-label">Success</div>
          </div>
          <div class="summary-card warning" v-if="outdatedCount > 0">
            <div class="card-value">{{ outdatedCount }}</div>
            <div class="card-label">Out of Date</div>
          </div>
          <div class="summary-card error" v-if="errorCount > 0">
            <div class="card-value">{{ errorCount }}</div>
            <div class="card-label">Errors</div>
          </div>
          <div class="summary-card retrying" v-if="retryingCount > 0">
            <div class="card-value">{{ retryingCount }}</div>
            <div class="card-label">Waiting for data</div>
          </div>
          <div class="summary-card running" v-if="runningCount > 0">
            <div class="card-value">{{ runningCount }}</div>
            <div class="card-label">Running</div>
          </div>
        </div>

        <!-- Filter bar -->
        <div class="filter-bar">
          <button class="btn btn-sm v2-toggle-btn" :class="v2Only ? 'btn-primary' : 'btn-outline-secondary'" @click="v2Only = !v2Only">
            V2 only
          </button>
          <input type="text" class="form-control form-control-sm filter-input" v-model="filterText" placeholder="Filter models..." />
          <select class="form-select form-select-sm filter-select" v-model="statusFilter">
            <option value="">All statuses</option>
            <option value="success">Success</option>
            <option value="OUTOFDATE">Out of date</option>
            <option value="aborted_no_data">Waiting for data</option>
            <option value="error">Error</option>
            <option value="running">Running</option>
          </select>
        </div>

        <!-- Status table -->
        <div class="table-wrapper">
          <table class="table table-sm table-hover status-table">
            <thead>
              <tr>
                <th @click="sortBy('friendlyName')" class="sortable">
                  Model <i class="bi" :class="getSortIcon('friendlyName')"></i>
                </th>
                <th @click="sortBy('runId')" class="sortable">
                  Run ID <i class="bi" :class="getSortIcon('runId')"></i>
                </th>
                <th @click="sortBy('status')" class="sortable">
                  Status <i class="bi" :class="getSortIcon('status')"></i>
                </th>
                <th @click="sortBy('currentStatus')" class="sortable">
                  Current Activity <i class="bi" :class="getSortIcon('currentStatus')"></i>
                </th>
                <th @click="sortBy('totalTimeSecs')" class="sortable text-end">
                  Total Time <i class="bi" :class="getSortIcon('totalTimeSecs')"></i>
                </th>
                <th @click="sortBy('processingTimeSecs')" class="sortable text-end">
                  Processing <i class="bi" :class="getSortIcon('processingTimeSecs')"></i>
                </th>
                <th @click="sortBy('waitTimeSecs')" class="sortable text-end">
                  Wait Time <i class="bi" :class="getSortIcon('waitTimeSecs')"></i>
                </th>
                <th @click="sortBy('endTimeEpoch')" class="sortable">
                  Completed <i class="bi" :class="getSortIcon('endTimeEpoch')"></i>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="loader in filteredLoaderData" :key="loader.type"
                  :class="getRowClass(loader)"
                  @click="toggleExpanded(loader.type)">
                <td class="model-name-cell">
                  <i class="bi bi-chevron-right expand-icon" :class="{ expanded: expandedRows.has(loader.type) }"></i>
                  <span class="status-dot" :class="getStatusDotClass(loader)"></span>
                  {{ loader.friendlyName }}
                  <span class="model-type-badge" v-if="loader.type !== loader.friendlyName">{{ loader.type }}</span>
                </td>
                <td class="run-id-cell">
                  <code v-if="loader.runId">{{ loader.runId }}</code>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <span class="status-badge" :class="getStatusBadgeClass(loader)">
                    {{ getStatusLabel(loader) }}
                  </span>
                </td>
                <td class="current-status-cell">
                  <template v-if="loader.currentStatus">
                    <span class="current-status-text">{{ loader.currentStatus }}</span>
                    <span v-if="loader.currentStatusTime" class="current-status-time text-muted">
                      {{ formatTimeAgo(loader.currentStatusTimeMs) }}
                    </span>
                  </template>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-end time-cell">{{ formatDuration(loader.totalTimeSecs) }}</td>
                <td class="text-end time-cell">{{ formatDuration(loader.processingTimeSecs) }}</td>
                <td class="text-end time-cell">{{ formatDuration(loader.waitTimeSecs) }}</td>
                <td class="completed-cell">
                  <span v-if="loader.endTimeEpoch" :title="formatFullDateTime(loader.endTimeEpoch)">
                    {{ formatTimeAgo(loader.endTimeEpoch * 1000) }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
              </tr>
              <!-- Expanded detail row -->
              <tr v-for="loader in filteredLoaderData" :key="loader.type + '_detail'"
                  v-show="expandedRows.has(loader.type)" class="detail-row">
                <td colspan="8">
                  <div class="detail-content">
                    <div class="detail-grid">
                      <div class="detail-item">
                        <span class="detail-label">Loader Type</span>
                        <span class="detail-value">{{ loader.type }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Run ID</span>
                        <span class="detail-value">{{ loader.runId || '-' }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Data Run ID</span>
                        <span class="detail-value">{{ loader.dataRunId || '-' }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">Start Time</span>
                        <span class="detail-value">{{ formatFullDateTime(loader.startTimeEpoch) }}</span>
                      </div>
                      <div class="detail-item">
                        <span class="detail-label">End Time</span>
                        <span class="detail-value">{{ formatFullDateTime(loader.endTimeEpoch) }}</span>
                      </div>
                      <div class="detail-item" v-if="loader.downloadWaitSecs != null">
                        <span class="detail-label">Download Wait</span>
                        <span class="detail-value">{{ formatDuration(loader.downloadWaitSecs) }}</span>
                      </div>
                      <div class="detail-item" v-if="loader.processingWaitSecs != null">
                        <span class="detail-label">Processing Wait</span>
                        <span class="detail-value">{{ formatDuration(loader.processingWaitSecs) }}</span>
                      </div>
                      <div class="detail-item full-width" v-if="loader.info">
                        <span class="detail-label">Info</span>
                        <span class="detail-value info-text">{{ loader.info }}</span>
                      </div>
                      <div class="detail-item full-width" v-if="loader.runInfo">
                        <span class="detail-label">Run Info</span>
                        <span class="detail-value info-text">{{ loader.runInfo }}</span>
                      </div>
                      <div class="detail-item full-width" v-if="loader.currentStatusDetail">
                        <span class="detail-label">Status Detail</span>
                        <span class="detail-value info-text">{{ loader.currentStatusDetail }}</span>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <div v-else class="text-center py-4 text-muted">
        <i class="bi bi-inbox fs-4"></i>
        <p class="mt-2">No model load status data available.</p>
      </div>
    </template>
  </div>
</template>

<script>
import "/src/visualcrossing-core/vccommunicator_serveradmin";

const FRIENDLY_NAMES = {
  'gfs_2': 'GFS',
  'gfs_location_2': 'GFS (Location)',
  'gfse_2': 'GFS Ensemble',
  'nam_2': 'NAM',
  'nam_location_2': 'NAM (Location)',
  'hrrr_2': 'HRRR',
  'hrrr_location_2': 'HRRR (Location)',
  'icon_eu_2': 'ICON-EU',
  'icon_eu_location_2': 'ICON-EU (Location)',
  'icon_global_2': 'ICON Global',
  'icon_global_location_2': 'ICON Global (Location)',
  'ecmwf_2': 'ECMWF',
  'ecmwf_location_2': 'ECMWF (Location)',
  'ukmo_global_2': 'UK Met Office',
  'ukmo_global_location_2': 'UK Met Office (Location)',
  'ndfd': 'NDFD',
  'ndfd_2': 'NDFD (V2)',
  'metar': 'METAR',
  'mesonet': 'Mesonet',
  'alerts_us': 'Alerts (US)',
  'alerts_ca': 'Alerts (Canada)',
  'alerts_europe': 'Alerts (Europe)',
  'nbm_2': 'NBM',
  'nbm_location_2': 'NBM (Location)',
  'cmc_gem_2': 'CMC GEM',
  'cmc_gem_location_2': 'CMC GEM (Location)',
  'rdps_2': 'RDPS',
  'rdps_location_2': 'RDPS (Location)',
  'ecmwf_aifs_2': 'ECMWF AIFS',
  'ecmwf_aifs_location_2': 'ECMWF AIFS (Location)',
  'ai_gfs_2': 'AI GFS',
  'ai_gfs_location_2': 'AI GFS (Location)',
  'mrms': 'MRMS',
  'mrms_2': 'MRMS (V2)',
  'goes': 'GOES',
  'goes_2': 'GOES (V2)',
  'vcblended': 'VC Blended',
  'vcblended_2': 'VC Blended (V2)',
  'vcblended_extended_2': 'VC Blended Extended',
};

export default {
  name: 'ModelLoadStatus',
  data() {
    return {
      sessionId: null,
      sessionState: null,
      loaderData: [],
      isLoading: false,
      errorMessage: null,
      lastRefreshTime: null,
      autoRefresh: true,
      refreshIntervalSecs: 30,
      refreshTimer: null,
      sortKey: 'friendlyName',
      sortOrder: 'asc',
      filterText: '',
      statusFilter: '',
      v2Only: true,
      expandedRows: new Set(),
      nowMs: Date.now(),
      nowTimer: null,
    };
  },
  computed: {
    hasSession() {
      return !!this.sessionState?.apiKey;
    },
    successCount() {
      return this.loaderData.filter(l => l.status === 'success').length;
    },
    errorCount() {
      return this.loaderData.filter(l => l.status && l.status !== 'success' && l.status !== 'OUTOFDATE' && l.status !== 'aborted_no_data' && !this.isRunning(l)).length;
    },
    outdatedCount() {
      return this.loaderData.filter(l => l.status === 'OUTOFDATE').length;
    },
    retryingCount() {
      return this.loaderData.filter(l => l.status === 'aborted_no_data').length;
    },
    runningCount() {
      return this.loaderData.filter(l => this.isRunning(l)).length;
    },
    filteredLoaderData() {
      let data = [...this.loaderData];

      if (this.v2Only) {
        data = data.filter(l => l.type.endsWith('_2'));
      }

      if (this.filterText) {
        const ft = this.filterText.toLowerCase();
        data = data.filter(l =>
          l.friendlyName.toLowerCase().includes(ft) ||
          l.type.toLowerCase().includes(ft) ||
          (l.runId && l.runId.toLowerCase().includes(ft))
        );
      }

      if (this.statusFilter) {
        if (this.statusFilter === 'running') {
          data = data.filter(l => this.isRunning(l));
        } else {
          data = data.filter(l => l.status === this.statusFilter);
        }
      }

      const order = this.sortOrder === 'asc' ? 1 : -1;
      data.sort((a, b) => {
        let va = a[this.sortKey];
        let vb = b[this.sortKey];
        // For currentStatus, push idle/empty to the end regardless of sort direction
        if (this.sortKey === 'currentStatus') {
          const aIdle = !va || va.toLowerCase() === 'idle';
          const bIdle = !vb || vb.toLowerCase() === 'idle';
          if (aIdle !== bIdle) return aIdle ? 1 : -1;
        }
        if (va == null) va = '';
        if (vb == null) vb = '';
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va < vb) return -order;
        if (va > vb) return order;
        return 0;
      });

      return data;
    },
  },
  methods: {
    isRunning(loader) {
      return loader.currentStatus && !loader.endTimeEpoch;
    },
    getFriendlyName(type) {
      if (FRIENDLY_NAMES[type]) return FRIENDLY_NAMES[type];
      // Generate a reasonable name from the type
      return type
        .replace(/_2$/, '')
        .replace(/_location$/, ' (Loc)')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
    },
    async refresh() {
      const vcCommunicator = window.vcCommunicator;
      if (!vcCommunicator || !this.hasSession) return;

      this.isLoading = true;
      this.errorMessage = null;

      try {
        const data = await vcCommunicator.loaderstatus();

        this.loaderData = (Array.isArray(data) ? data : []).map(item => {
          const totalTimeSecs = item.totalTimeSecs ?? null;
          const waitTimeSecs = item.waitTimeSecs ?? null;
          const processingTimeSecs = (totalTimeSecs != null && waitTimeSecs != null)
            ? Math.max(0, totalTimeSecs - waitTimeSecs)
            : null;

          return {
            ...item,
            friendlyName: this.getFriendlyName(item.type),
            totalTimeSecs,
            waitTimeSecs,
            processingTimeSecs,
            downloadWaitSecs: item.downloadWaitSecs ?? null,
            processingWaitSecs: item.processingWaitSecs ?? null,
            currentStatusTimeMs: item.currentStatusTimeEpoch ? item.currentStatusTimeEpoch * 1000 : null,
          };
        });

        this.lastRefreshTime = Date.now();
      } catch (err) {
        console.error('Failed to fetch loader status:', err);
        this.errorMessage = typeof err === 'string' ? err : (err.message || 'Failed to fetch model status');
      } finally {
        this.isLoading = false;
      }
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      if (this.autoRefresh) {
        this.refreshTimer = setInterval(() => {
          this.refresh();
        }, this.refreshIntervalSecs * 1000);
      }
    },
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortOrder = 'asc';
      }
    },
    getSortIcon(key) {
      if (this.sortKey !== key) return 'bi-chevron-expand';
      return this.sortOrder === 'asc' ? 'bi-sort-up' : 'bi-sort-down';
    },
    toggleExpanded(type) {
      if (this.expandedRows.has(type)) {
        this.expandedRows.delete(type);
      } else {
        this.expandedRows.add(type);
      }
      // Force reactivity
      this.expandedRows = new Set(this.expandedRows);
    },
    getRowClass(loader) {
      const classes = ['status-row'];
      if (loader.status === 'OUTOFDATE') classes.push('row-outdated');
      else if (loader.status === 'aborted_no_data') classes.push('row-retrying');
      else if (loader.status !== 'success') classes.push('row-error');
      if (this.isRunning(loader)) classes.push('row-running');
      return classes;
    },
    getStatusDotClass(loader) {
      if (this.isRunning(loader)) return 'dot-running';
      if (loader.status === 'success') return 'dot-success';
      if (loader.status === 'OUTOFDATE') return 'dot-warning';
      if (loader.status === 'aborted_no_data') return 'dot-retrying';
      return 'dot-error';
    },
    getStatusBadgeClass(loader) {
      if (this.isRunning(loader)) return 'badge-running';
      if (loader.status === 'success') return 'badge-success';
      if (loader.status === 'OUTOFDATE') return 'badge-warning';
      if (loader.status === 'aborted_no_data') return 'badge-retrying';
      return 'badge-error';
    },
    getStatusLabel(loader) {
      if (this.isRunning(loader)) return 'Running';
      if (loader.status === 'success') return 'OK';
      if (loader.status === 'OUTOFDATE') return 'Outdated';
      if (loader.status === 'aborted_no_data') return 'Waiting for data';
      return loader.status || 'Unknown';
    },
    formatDuration(secs) {
      if (secs == null) return '-';
      if (secs < 60) return `${secs}s`;
      if (secs < 3600) {
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return s > 0 ? `${m}m ${s}s` : `${m}m`;
      }
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      return m > 0 ? `${h}h ${m}m` : `${h}h`;
    },
    formatTimeAgo(epochMs) {
      if (!epochMs) return '';
      const diff = this.nowMs - epochMs;
      if (diff < 0) return 'just now';
      const secs = Math.floor(diff / 1000);
      if (secs < 60) return `${secs}s ago`;
      const mins = Math.floor(secs / 60);
      if (mins < 60) return `${mins}m ago`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `${hours}h ${mins % 60}m ago`;
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h ago`;
    },
    formatFullDateTime(epochSecs) {
      if (!epochSecs) return '-';
      const d = new Date(epochSecs * 1000);
      return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      });
    },
  },
  watch: {
    autoRefresh(val) {
      if (val) {
        this.startAutoRefresh();
      } else {
        this.stopAutoRefresh();
      }
    },
  },
  mounted() {
    const vcCommunicator = window.vcCommunicator;
    if (vcCommunicator) {
      this.sessionId = vcCommunicator.sessionId;
      this.sessionState = vcCommunicator.sessionState;

      vcCommunicator.addHandler('createsession', (sessionId, sessionState) => {
        this.sessionId = sessionId;
        this.sessionState = sessionState;
        this.refresh();
      });

      vcCommunicator.addHandler('terminatesession', () => {
        this.sessionId = null;
        this.sessionState = null;
        this.loaderData = [];
      });

      if (this.hasSession) {
        this.refresh();
      }
    }

    this.startAutoRefresh();

    // Update "time ago" displays
    this.nowTimer = setInterval(() => {
      this.nowMs = Date.now();
    }, 5000);
  },
  unmounted() {
    this.stopAutoRefresh();
    if (this.nowTimer) {
      clearInterval(this.nowTimer);
    }
  },
};
</script>

<style scoped>
.model-load-status {
  font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  padding: 16px;
  max-width: 1400px;
  margin: 0 auto;
  font-size: 13px;
  color: #333;
}

.status-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
}

.status-header h5 {
  margin: 0;
  font-weight: 600;
  font-size: 18px;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.auto-refresh-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #666;
  cursor: pointer;
}

.last-refresh {
  font-size: 11px;
}

/* Summary cards */
.summary-cards {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.summary-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px 20px;
  text-align: center;
  min-width: 80px;
}

.summary-card .card-value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

.summary-card .card-label {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-card.success { border-color: #28a745; background: #f0fff4; }
.summary-card.success .card-value { color: #28a745; }
.summary-card.warning { border-color: #ffc107; background: #fffdf0; }
.summary-card.warning .card-value { color: #c29200; }
.summary-card.error { border-color: #dc3545; background: #fff5f5; }
.summary-card.error .card-value { color: #dc3545; }
.summary-card.retrying { border-color: #6c757d; background: #f5f5ff; }
.summary-card.retrying .card-value { color: #6c757d; }
.summary-card.running { border-color: #007bff; background: #f0f7ff; }
.summary-card.running .card-value { color: #007bff; }

/* Filter bar */
.filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.v2-toggle-btn {
  white-space: nowrap;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 12px;
  border-radius: 14px;
}

.filter-input {
  max-width: 250px;
}

.filter-select {
  max-width: 160px;
}

/* Table */
.table-wrapper {
  overflow-x: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.status-table {
  margin: 0;
  font-size: 12.5px;
}

.status-table thead th {
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
  white-space: nowrap;
  padding: 8px 12px;
  position: sticky;
  top: 0;
  z-index: 1;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  color: #333;
}

.sortable .bi {
  font-size: 10px;
  opacity: 0.5;
}

.status-table tbody td {
  padding: 6px 12px;
  vertical-align: middle;
  border-color: #f0f0f0;
}

.status-row {
  cursor: pointer;
  transition: background-color 0.15s;
}

.status-row:hover {
  background-color: #f8f9fa !important;
}

.row-outdated {
  background-color: #fffdf0;
}

.row-retrying {
  background-color: #f5f5ff;
}

.row-error {
  background-color: #fff8f8;
}

.row-running {
  background-color: #f0f7ff;
}

/* Model name cell */
.model-name-cell {
  font-weight: 500;
  white-space: nowrap;
}

.expand-icon {
  display: inline-block;
  font-size: 10px;
  margin-right: 4px;
  transition: transform 0.15s;
  opacity: 0.4;
}

.expand-icon.expanded {
  transform: rotate(90deg);
}

.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

.dot-success { background: #28a745; }
.dot-warning { background: #ffc107; }
.dot-error { background: #dc3545; }
.dot-retrying { background: #6c757d; }
.dot-running { background: #007bff; animation: pulse 1.5s infinite; }

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.model-type-badge {
  font-size: 10px;
  color: #999;
  margin-left: 6px;
  font-weight: 400;
}

/* Run ID */
.run-id-cell code {
  font-size: 11px;
  background: #f0f0f0;
  padding: 1px 5px;
  border-radius: 3px;
  color: #555;
}

/* Status badge */
.status-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.badge-success { background: #d4edda; color: #155724; }
.badge-warning { background: #fff3cd; color: #856404; }
.badge-error { background: #f8d7da; color: #721c24; }
.badge-retrying { background: #e2e3e5; color: #383d41; }
.badge-running { background: #cce5ff; color: #004085; }

/* Current status */
.current-status-cell {
  max-width: 200px;
}

.current-status-text {
  font-size: 11.5px;
}

.current-status-time {
  display: block;
  font-size: 10px;
}

/* Time cells */
.time-cell {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.completed-cell {
  white-space: nowrap;
  font-size: 12px;
}

/* Detail row */
.detail-row {
  background: #fafbfc !important;
}

.detail-row:hover {
  background: #fafbfc !important;
}

.detail-content {
  padding: 8px 24px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px 24px;
}

.detail-item {
  display: flex;
  flex-direction: column;
}

.detail-item.full-width {
  grid-column: 1 / -1;
}

.detail-label {
  font-size: 10px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.detail-value {
  font-size: 12px;
  color: #333;
}

.info-text {
  font-family: monospace;
  font-size: 11px;
  word-break: break-all;
  background: #f0f0f0;
  padding: 4px 6px;
  border-radius: 3px;
}

/* Spinner */
.spin-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
